/**
 * @lumi/ui — Suite-wide light/dark theme.
 *
 * The theme is applied by setting `data-theme="light|dark"` on <html>;
 * tokens/colors.css carries the dark overrides, so every app re-themes
 * through the shared `var(--…)` tokens with no per-component work.
 *
 * Resolution order (same idea as the `kb_locale` handoff): a `?theme=`
 * URL param wins (cross-subdomain handoff from the workspace shell),
 * then this origin's saved choice (`kb_theme`), then the OS preference.
 *
 * For no-flash boot each app's index.html runs a tiny inline script that
 * applies + persists `data-theme` before paint. The reactive `theme` ref is
 * therefore seeded FROM `<html data-theme>` at module load — the value that
 * script already committed to — so the ref can never disagree with what the
 * user is looking at. Only when the attribute is absent (SSR, tests, an app
 * without the script) does it fall back to the resolution order above, and
 * then it writes what it resolved back to the DOM. Nothing an app has to
 * remember to call: initTheme() stays exported and idempotent for the callers
 * that have it, but forgetting it is no longer a bug (it used to leave the ref
 * at a hard-coded 'light' while <html> said dark — the first toggle click then
 * re-wrote dark and did nothing visible).
 */
import { ref } from 'vue';

export const THEMES = ['light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'kb_theme';

function isTheme(v: string | null): v is Theme {
  return v === 'light' || v === 'dark';
}

function systemPref(): Theme {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/** The `?theme=` cross-origin handoff param, when it names a real theme. */
function fromUrl(): Theme | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('theme');
  return isTheme(value) ? value : null;
}

/** This origin's saved choice. */
function fromStorage(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isTheme(saved) ? saved : null;
  } catch {
    return null;
  }
}

function apply(value: Theme) {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = value;
    // The browser's bars on a phone (theme-color) follow the theme chosen here,
    // not the OS one their media queries guess at: an app's index.html carries
    // one per theme, keyed by `data-theme`, and the chosen one is switched on.
    document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"][data-theme]').forEach((meta) => {
      meta.setAttribute('media', meta.dataset.theme === value ? 'all' : 'not all');
    });
  }
}

/** What <html data-theme> currently says, or null when it says nothing usable. */
function fromDom(): Theme | null {
  if (typeof document === 'undefined') return null;
  const current = document.documentElement.dataset.theme ?? null;
  return isTheme(current) ? current : null;
}

function persist(value: Theme) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). The theme is
    // still applied; only the memory of it is lost. This runs at module load,
    // so a throw here would take the whole app down with it.
  }
}

/**
 * The theme as it actually IS: what the pre-paint script put on <html>, else
 * the resolution order. Applies + persists so DOM, storage and ref agree.
 */
function syncFromDom(): Theme {
  // `?theme=` still wins, ahead of the DOM: the documented resolution order is
  // a contract the cross-origin handoff depends on, and the pre-paint script
  // reads the same param first, so in practice the two already agree. The DOM
  // slots in ahead of kb_theme/OS because it IS this page's committed state.
  const value = fromUrl() ?? fromDom() ?? fromStorage() ?? systemPref();
  apply(value);
  persist(value);
  return value;
}

/**
 * Reactive current theme. Drive UI off this (e.g. the header toggle).
 * Seeded at module load from the DOM (see syncFromDom) — importing this module
 * is the only thing an app must do for the ref to be right.
 */
export const theme = ref<Theme>(syncFromDom());

/**
 * Call once on app boot. Applies `data-theme` to <html>, persists the choice
 * under this origin, and re-syncs the reactive ref. Idempotent, and no longer
 * load-bearing: the ref is already seeded at module load. Kept because callers
 * exist and because an app may want to re-assert the theme after boot.
 */
export function initTheme(): Theme {
  const value = syncFromDom();
  theme.value = value;
  return value;
}

export function setTheme(next: Theme) {
  theme.value = next;
  apply(next);
  persist(next);
}

export function toggleTheme() {
  setTheme(theme.value === 'dark' ? 'light' : 'dark');
}

export function useTheme() {
  return { theme, setTheme, toggleTheme };
}
